import type { ApiEnvelope, ApiErrorPayload, FanToArtistMessagePage } from "@kira/contracts";

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string | undefined;

  constructor(message: string, status: number, code = "UNKNOWN", traceId?: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.traceId = traceId;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  timeoutMs?: number;
  idempotencyKey?: string;
}

const safeMessage = (status: number, code?: string) => {
  const campaignErrors: Record<string, string> = {
    CAMPAIGN_NOT_FOUND: "企划不存在或不可访问", CAMPAIGN_NOT_OPEN: "企划暂未开放参与", CAMPAIGN_CLOSED: "企划已经截止，不能再变更参与",
    MATERIAL_REQUIRED: "请选择一款物料", MATERIAL_SOLD_OUT: "这款物料已领完，请选择其他物料", ALREADY_CLAIMED: "每人限领一份，请先放弃当前物料",
  };
  if (code && campaignErrors[code]) return campaignErrors[code];
  if (code === "DEMO_USER_NOT_FOUND") return "没有这个演示账号，请选择一位可用身份";
  if (code === "NICKNAME_TAKEN") return "这个昵称已经有人使用了，换一个试试";
  if (code === "PHONE_TAKEN") return "这个手机号已经注册过了，请换一个手机号";
  if (code === "ACCOUNT_DISABLED") return "这个账号暂时无法使用";
  if (code === "CAMPAIGN_DEADLINE_INVALID") return "截止时间需要晚于当前时间";
  if (code === "CAMPAIGN_DEADLINE_PASSED") return "企划截止时间已过，请重新创建后提交";
  if (status === 401) return "登录状态已失效，请重新进入";
  if (status === 403) return "当前账号没有执行此操作的权限";
  if (status === 404) return "没有找到对应内容，请刷新后再试";
  if (status === 409) return "状态刚刚发生变化，请刷新后再试";
  if (status === 400 || status === 422) return "填写内容有误，请检查后再试";
  if (status === 429) return "操作太频繁，请稍后再试";
  if (status >= 500) return "服务暂时不可用，请稍后重试";
  return "请求没有完成，请稍后重试";
};

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, timeoutMs, idempotencyKey, ...requestOptions } = options;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs ?? 8_000);
  const headers = new Headers(requestOptions.headers);
  if (body !== undefined) headers.set("Content-Type", "application/json");
  if (idempotencyKey) headers.set("Idempotency-Key", idempotencyKey);

  try {
    const requestInit: RequestInit = {
      ...requestOptions,
      credentials: "include",
      headers,
      signal: controller.signal,
    };
    if (body !== undefined) requestInit.body = JSON.stringify(body);
    const response = await fetch(path, requestInit);

    if (!response.ok) {
      let payload: ApiErrorPayload | undefined;
      try {
        payload = (await response.json()) as ApiErrorPayload;
      } catch (error) {
        reportClientError(error, { path, phase: "decode-error" });
      }
      throw new ApiClientError(
        safeMessage(response.status, payload?.error?.code),
        response.status,
        payload?.error?.code ?? "HTTP_ERROR",
        payload?.traceId,
      );
    }

    const payload = (await response.json()) as ApiEnvelope<T>;
    return payload.data;
  } catch (error) {
    if (error instanceof ApiClientError) throw error;
    reportClientError(error, { path, phase: "request" });
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiClientError("连接超时，请稍后重试", 0, "REQUEST_TIMEOUT");
    }
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    throw new ApiClientError(offline ? "当前网络不可用，请检查连接后重试" : "暂时无法连接服务，请稍后重试", 0, "NETWORK_ERROR");
  } finally {
    window.clearTimeout(timeout);
  }
}

export function createIdempotencyKey(scope: string): string {
  return `${scope}:${crypto.randomUUID()}`;
}

export function getFanToArtistMessageHistory(
  artistId: string,
  options: { before?: string; limit?: number } = {},
): Promise<FanToArtistMessagePage> {
  const query = new URLSearchParams();
  if (options.before) query.set("before", options.before);
  if (options.limit !== undefined) query.set("limit", String(options.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  return apiRequest<FanToArtistMessagePage>(
    `/api/fan/artist-messages/${encodeURIComponent(artistId)}${suffix}`,
  );
}

export function reportClientError(error: unknown, context: Record<string, unknown> = {}): void {
  const normalized = error instanceof Error ? error : new Error(String(error));
  window.dispatchEvent(
    new CustomEvent("kira:client-error", {
      detail: { message: normalized.message, stack: normalized.stack, context },
    }),
  );
  const meta = import.meta as ImportMeta & { env?: { DEV?: boolean } };
  if (meta.env?.DEV) console.error("[Kira client error]", normalized, context);
}
